import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import {
  useEffect,
  useState,
} from "react";
import type {
  Company,
  Store,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import Description from "~icons/material-symbols/description-outline";
import SyncAlt from "~icons/material-symbols/sync-alt";
import {
  ConsoleShell,
  type ConsoleNavItem,
} from "../components/v2";
import { getTenantLabelAppearance } from "../components/tenant-label";
import {
  STORE_TOKEN_STORAGE_KEY,
  requireStoreAuth,
} from "../libs/auth";
import { type StoreClient, createStoreClient } from "../libs/client";

type LoaderData = {
  client: StoreClient;
  company: Company;
  store: Store;
};

export const Route = createFileRoute("/store")({
  beforeLoad: async (): Promise<LoaderData> => {
    const token = requireStoreAuth();
    const client = createStoreClient({ token });
    const myStore = await client.getMyStore({});

    if (!myStore.storeWithCompany?.store) {
      throw new Error("store is not found");
    }

    if (!myStore.storeWithCompany.company) {
      throw new Error("company is not found");
    }

    return {
      client,
      company: myStore.storeWithCompany.company,
      store: myStore.storeWithCompany.store,
    };
  },
  component: RouteComponent,
});

function getIsDesktop() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }

  return window.matchMedia("(min-width: 768px)").matches;
}

function RouteComponent() {
  const { store } = Route.useRouteContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(getIsDesktop);
  const tenantLabel = getTenantLabelAppearance().name || "地域ポイント";
  const navItems: ConsoleNavItem[] = [
    {
      icon: <Description />,
      label: "登録情報",
      to: "/store/dashboard",
    },
    {
      icon: <SyncAlt />,
      label: "取引履歴",
      to: "/store/transactions",
    },
  ];

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
      if (event.matches) {
        setIsMenuOpen(false);
      }
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(STORE_TOKEN_STORAGE_KEY);
    navigate({ to: "/" });
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <ConsoleShell
      currentPathname={location.pathname}
      isDesktop={isDesktop}
      isMenuOpen={isMenuOpen}
      navItems={navItems}
      onCloseMenu={handleCloseMenu}
      onLogout={handleLogout}
      onToggleMenu={() => setIsMenuOpen((prev) => !prev)}
      storeName={store.name}
      tenantLabel={tenantLabel}
    >
      <Outlet />
    </ConsoleShell>
  );
}
